import { z } from 'zod';
import { ZBoundingBox2D, ZCoordinate, ZCoordinate2D, ZPolygon2D } from './foundationSchema';
export const ZPoint3D = z.array(z.number()).min(3).max(3).describe('3D point represented as [x, y, z]');
export const ZLine3D = z.array(ZPoint3D).min(2).max(2).describe('3D line represented as [startPoint, endPoint]');
/**
 * Basic 3D vector
 */
const IVec3 = z.object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
});
/**
 * 3D OBB in world millimeters (Mm fields) and meters (M fields, correct unit).
 * The Mm-suffixed fields are misnamed — they actually contain meters. Use M fields going forward.
 */
const IObb3DWorldMm = z.object({
    centerMm: IVec3,
    centerM: IVec3.optional(),
    axisA: IVec3,
    axisB: IVec3,
    axisC: IVec3,
    extentAMm: z.number(),
    extentBMm: z.number(),
    extentCMm: z.number(),
    extentAM: z.number().optional(),
    extentBM: z.number().optional(),
    extentCM: z.number().optional(),
});
/**
 * Heights in mm (bottom/middle/top).
 * Despite the Mm name, values are actually in meters. Use ZHeightTripletM going forward.
 */
const ZHeightTripletMm = z.object({
    bottom: z.number(),
    middle: z.number(),
    top: z.number(),
});
/** Heights in meters (bottom/middle/top) — correctly named successor to ZHeightTripletMm. */
const ZHeightTripletM = z.object({
    bottom: z.number(),
    middle: z.number(),
    top: z.number(),
});
const ZFloorSource = z.enum(['buildingStorey', 'unknown']);
/**
 * All geometry-related attributes.
 * Designed to support:
 * - 2D plan rendering + Mongo spatial queries (polygon/bbox/center)
 * - 3D queries/viewers (AABB + heights)
 */
export const ZGeometry = z.object({
    // 2D (world XY)
    polygon2d: ZPolygon2D.optional(), // footprint polygon
    bbox2d: ZBoundingBox2D.optional(), // rotated or axis-aligned 2D bbox
    centerPoint2d: ZCoordinate2D.optional(), // useful for labels + fallback
    // 3D (world XYZ)
    Obb3DWorldMmList: z.array(IObb3DWorldMm).optional(),
    // Derived Z values — Mm fields are misnamed (actually meters). Use M fields going forward.
    zWorldMm: ZHeightTripletMm.optional(),
    zWorldM: ZHeightTripletM.optional(),
    heightMm: z.number().optional(),
    heightM: z.number().optional(),
    // Floor reference + relative heights
    floorZWorldMm: z.number().optional(),
    floorZWorldM: z.number().optional(),
    floorSource: ZFloorSource.optional(),
    zRelMm: ZHeightTripletMm.optional(),
    zRelM: ZHeightTripletM.optional(),
});
export const ZBuildingStory = z.object({
    elevation: z.number().optional(),
    expressId: z.number().optional(),
    globalId: z.string(),
    name: z.string(),
});
export const ZIfcType = z.object({
    globalId: z.string(),
    name: z.string(),
    typeClass: z.string(),
    predefinedType: z.union([z.string(), z.number()]).optional().nullable(),
});
export const ZSystem = z.object({
    globalId: z.string(),
    name: z.string(),
    typeClass: z.string(),
    predefinedType: z.union([z.string(), z.number()]).optional().nullable(),
});
export const ZElementMaterial = z.object({
    name: z.string(),
    thickness: z.number(), // mm
    calculatedVolume: z.number().optional(), // m3
});
export const ZCeilingGridItem = z.object({
    lines: z.array(ZLine3D),
    intersectionPoint: ZPoint3D.optional(),
    direction: ZPoint3D.optional(),
    // Previously tuple; now OpenAPI-friendly array
    tileSize: z.array(z.number()).min(2).max(2).optional(),
    interiorLines: z.array(ZLine3D).optional(),
    // Previously z.array(z.tuple([number, number]))
    edgePolygon: z.array(ZCoordinate2D).optional(),
    floorHeightMm: z.number().optional(),
    floorHeightM: z.number().optional(),
    worldHeightMm: z.number().optional(),
    worldHeightM: z.number().optional(),
});
export const ZMcBimItem = z.object({
    _id: z.any(),
    globalId: z.string(),
    // Identity / context
    buildingStory: ZBuildingStory.nullable(),
    instanceClass: z.string(),
    expressId: z.number(),
    predefinedType: z.union([z.string(), z.number()]).optional().nullable(),
    // Geometry (new)
    geometry: ZGeometry.optional(),
    geometryVersion: z.number().optional(), // e.g. 1 when geometry.* is populated
    // Other fields
    fileAttr1: z.string().optional(),
    fileAttr2: z.string().optional(),
    fileAttr3: z.string().optional(),
    fileAttr4: z.string().optional(),
    fileFolderId: z.string().optional(),
    fileIfcSchema: z.string(),
    fileIsLatest: z.boolean(),
    fileLastModifiedBy: z.string(),
    fileName: z.string(),
    filePath: z.string(),
    fileStorageId: z.string(),
    fileUploadedAt: z.string().or(z.date()),
    includedMaterials: z.record(z.boolean()).optional(),
    insertedAt: z.string().or(z.date()),
    longName: z.string().optional(),
    materials: z.array(z.record(z.number())).optional(),
    name: z.string(),
    description: z.string(),
    building: z.string().optional(),
    job: z.object({
        jobId: z.any(),
        createdAt: z.string().or(z.date()),
    }),
    /**
     * Legacy (kept during migration)
     * Recommendation: write new data to geometry.*,
     * and optionally mirror to these until all consumers are migrated.
     */
    bbox: z.array(ZCoordinate).optional(),
    polygon: z.array(ZCoordinate).min(4).optional(),
    centerPoint: ZCoordinate.optional(),
    psets: z.record(z.record(z.string().or(z.number()).or(z.boolean()))),
    type: ZIfcType.nullable(),
    ceilingGrids: ZCeilingGridItem.array().optional(),
    error2d: z
        .object({
        message: z.string(),
        stack: z.string(),
    })
        .optional(),
    geometryExtractedFromTypeGlobalId: z.string().optional(),
    elementMaterials: z.array(ZElementMaterial).optional(),
    primarySystem: ZSystem.optional().nullable().optional(),
    systems: z.array(ZSystem).optional(),
});
